import React, { memo, useCallback, useEffect } from 'react';
import { AppState, wrapper } from '@/store';
import { ServerContext } from '@/type/pages';
import classNames from 'classnames';
import EditorChoice from '@/components/common/editor-choice';
import styles from '@/styles/pages/search.module.scss';
import SearchInput from '@/components/common/search-input';
import { Pagination } from 'antd';
import type { PaginationProps } from 'antd';
import SortFilter from '@/components/common/sort-filter';
import { getCateTagsAction, getProductTagsAction } from '@/store/actions/categoryPageAction';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getSearchAction } from '@/store/actions/searchPageAction';
import { filterSvgPath, getTopicAndProductTags } from '@/utils/dataFormat';
import NProgress from 'nprogress';
import { changeIsOpen, changeKeyword, changePage } from '@/store/slices/sortFilterSlice';
import { resetSortFilter } from '@/store/actions/sortFilterAction';
import dayjs from 'dayjs';
import Image from 'next/image';
import { getAllCategory, getAllCategoryLanding, getCateTags, getProductTags } from '@/services/category';
import { getMultiplePopupBanner, getPromotionBanner } from '@/services/contentstack';
import { getHeaderAndFooter } from '@/services/headerAndFooter';
import { HEADER_FOOTER_CONTENT_TYPE } from '@/constants/headerAndFooter';
import { headerAndFooterIncludeReference } from '@/constants/homePage';
import { changeDefaultData } from '@/pages/blog';
import { changeCateTags, changeProductTags } from '@/store/slices/categoryPageSlice';
import { getSearch } from '@/services/search';
import { changeSearch } from '@/store/slices/searchPageSlice';

const itemRender: PaginationProps['itemRender'] = (page: any, type, originalElement) => {
  if (type === 'prev') {
    return <Image src="/blog/icon/Left.svg" alt={''} width={9} height={14} />;
  }
  if (type === 'next') {
    return <Image src="/blog/icon/Right.svg" alt={''} width={9} height={14} />;
  }

  if (type === 'jump-prev') {
    return (
      <>
        <Image src="/blog/icon/JumpForwardArrow.png" alt={''} width={24} height={24} />
        <Image src="/blog/icon/JumpBackArrowDoubleLeft.png" alt={''} width={24} height={24} />
      </>
    );
  }

  if (type === 'jump-next') {
    return (
      <>
        <Image src="/blog/icon/JumpForwardArrow.png" alt={''} width={24} height={24} />
        <Image src="/blog/icon/JumpForwardArrowDouble.png" alt={''} width={24} height={24} />
      </>
    );
  }

  return originalElement;
};

const itemRenderOne: PaginationProps['itemRender'] = (page: any, type, originalElement) => {
  if (type === 'prev') {
    return <span className="Pagination_Prev"></span>;
  }
  if (type === 'next') {
    return <span className="Pagination_Next"></span>;
  }
  return originalElement;
};

export default memo(({ query }: any) => {
  const dispatch = useDispatch<any>();
  const { search, sortFilter, categoryPage, language } = useSelector(
    (state: AppState) => ({
      search: state.searchPage.search,
      sortFilter: state.sortFilter,
      categoryPage: state.categoryPage,
      language: state.homePage.language,
    }),
    shallowEqual,
  );
  const { keyword, topicTagEntryIds, prodTagEntryIds, startTimestamp, endTimestamp, sortBy } = sortFilter;
  const { cateTags, productTags } = categoryPage;
  const handleTime = (timestamp: number) => {
    const dayjsObject = dayjs.unix(timestamp);
    return dayjsObject.format('YYYY-MM-DD');
  };

  const handleSearch = useCallback(
    async (pageNumber: number, fromSort: boolean = false, sortByKey: string = '') => {
      let params = '';

      if (keyword) {
        params = `${params ? '&' : '?'}q=${keyword}`;
      }

      const resSortKey = fromSort ? sortByKey : sortBy;
      if (resSortKey) {
        params = params + `${params ? '&' : '?'}odri=${resSortKey}`;
      }

      const allTopics: any[] = [];

      cateTags.forEach((item: any) => {
        if (!Array.isArray(item.topics) || item.topics.length === 0) return;
        allTopics.push(...item.topics);
      });

      const tags = [...topicTagEntryIds, ...prodTagEntryIds];
      if (Array.isArray(tags) && tags.length > 0) {
        const tagTitles = [...allTopics, ...productTags]
          .filter((item: any) => tags.includes(item.uid))
          .map((item: any) => item?.url);
        if (Array.isArray(tagTitles) && tagTitles.length > 0) {
          const topicStr = `${params ? '&' : '?'}tagf=${tagTitles.join('+')}`;
          params = params + topicStr;
        }
      }

      if (startTimestamp) {
        const starttime = handleTime(startTimestamp);
        const endTime = handleTime(endTimestamp);
        const timeStamp = `${params ? '&' : '?'}sd=${starttime}&ed=${endTime}`;
        params = params + timeStamp;
      }
      const url = params ? `/${language.origin}/blog/search` + params : `/${language.origin}/blog/search/`;
      window.history.pushState(null, '', url);

      NProgress.start();
      await dispatch(changePage(pageNumber));

      await dispatch(
        getSearchAction({
          ...sortFilter,
          page: pageNumber,
          language: language.label,
          sortBy: resSortKey,
        }),
      );
      await dispatch(changeIsOpen(false));
      NProgress.done();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [
      cateTags,
      dispatch,
      endTimestamp,
      keyword,
      language.label,
      language.origin,
      prodTagEntryIds,
      productTags,
      sortBy,
      sortFilter,
      startTimestamp,
      topicTagEntryIds,
    ],
  );

  const handleChangePagination = async (pageNumber: number) => {
    await handleSearch(pageNumber);
  };

  const handleSort = async (sort: string) => {
    await handleSearch(1, true, sort);
  };

  useEffect(() => {
    setTimeout(() => {
      window?.dataLayer?.push({
        event: 'content_landing',
        searchterm: query.q,
        description: '',
        publish_time: '',
        subtitle: '',
        title: '',
        tags: '',
        pageURL: window.location.href,
        content_Group: 'search',
      });
    }, 3000);
  }, []);

  const handleReset = async () => {
    window.history.pushState(
      null,
      '',
      keyword ? `/${language.origin}/blog/search?q=${keyword}` : `/${language.origin}/blog/search/`,
    );
    NProgress.start();
    await dispatch(
      resetSortFilter({
        tags: '',
      }),
    );
    await dispatch(
      getSearchAction({
        ...sortFilter,
        page: 1,
        topicTagEntryIds: [],
        language: language.label,
        prodTagEntryIds: [],
        sortBy: 'publishDate',
      }),
    );
    NProgress.done();
  };

  return (
    <div className={styles.Search}>
      <div className={classNames(styles.MuiContainer_root, styles.MuiContainer_maxWidthXl)}>
        <div className={styles.Search_Input_Wrap}>
          <SearchInput
            placeholder={language.origin === 'zh' ? '搜索相關日誌' : 'Search Blog'}
            className={styles.Search_Input}
            onClick={() => handleSearch(1)}
          />
          <SortFilter
            className={styles.Sorting_Filtering}
            tags={query.tags || query.t}
            onSearch={() => handleSearch(1)}
            onReset={handleReset}
            onSort={handleSort}
            language={language.origin}
          />
        </div>
      </div>
      <>
        <div className={styles.Editor_Choice_Wrap}>
          {search.total > 0 ? (
            <div className={classNames(styles.MuiContainer_root, styles.MuiContainer_maxWidthXl)}>
              <div className={styles.Result_Desc}>
                {search.total}
                {language.origin === 'zh' ? ' 項搜尋結果' : search.total == 1 ? ' result found' : ' results found'}
              </div>
              <div className={styles.Editor_Choice_List}>
                {Array.isArray(search.records) &&
                  search.records.map((record: any) => (
                    <EditorChoice key={record.uid} className={styles.Editor_Choice} pageData={record} />
                  ))}
              </div>
            </div>
          ) : (
            <div className={styles.No_Result}>
              <div className={styles.Wrapper}>
                <Image src="/blog/icon/NoResult.svg" className={styles.Icon} alt={''} width={100} height={100} />
                {language.origin === 'zh' ? (
                  <p className={styles.Content}>沒有搜尋結果</p>
                ) : (
                  <p className={styles.Content}>No results</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={classNames(styles.MuiContainer_root, styles.MuiContainer_maxWidthXl, styles.Pagination)}>
          {search && search.total > 0 && search.totalPage && search.totalPage > 1 ? (
            <Pagination
              size="small"
              current={search.page}
              total={search.total}
              showSizeChanger={false}
              onChange={handleChangePagination}
              showTitle={false}
              itemRender={itemRender}
            />
          ) : search.total > 0 ? (
            <Pagination
              size="small"
              current={search.page}
              total={search.total}
              showSizeChanger={false}
              onChange={handleChangePagination}
              itemRender={itemRenderOne}
              showTitle={false}
            />
          ) : (
            ''
          )}
        </div>
      </>
    </div>
  );
});

export const getServerSideProps = wrapper.getServerSideProps(store => async (ctx: ServerContext) => {
  const language = (ctx.query.language as string) ?? null;
  const localLanguage = language === 'en' ? 'en-us' : 'zh-hk';
  let promotionBannerData;
  let popUpBannerData;
  const isSvg = filterSvgPath(ctx.req.url);
  if (!isSvg) {
    const lang = language === 'en' ? 'en-us' : 'zh-hk';
    const pathname = '/blog/search';

    const [result1, result2, result3, result4, result5, result6, result7]: any[] = await Promise.all([
      getAllCategory(lang),
      getAllCategoryLanding(lang),
      getPromotionBanner(pathname, lang),
      getMultiplePopupBanner(pathname, lang),
      getHeaderAndFooter(HEADER_FOOTER_CONTENT_TYPE, headerAndFooterIncludeReference, lang),
      getCateTags(lang),
      getProductTags(lang),
    ]);

    changeDefaultData(store, result1, result2, result3, result4, result5);

    // save promotionBannerData & popupBannerData for new layout by Emma-2024.5.30
    promotionBannerData = result3?.data;
    popUpBannerData =
      Array.isArray(result4?.data) && Array.isArray(result4?.data[0]) && result4?.data[0].length > 0
        ? result4?.data[0][0]
        : {};

    const cateTags1 = Object.entries(result6?.data).map(([title, topics]) => ({ title, topics }));
    store.dispatch(changeCateTags(cateTags1));
    store.dispatch(changeProductTags(result7?.data[0] || []));

    const keyword = (ctx.query.q as string) || '';
    store.dispatch(changeKeyword(keyword || ''));

    const t = ctx.query.t as string;
    let tags = ctx.query.tags as string;

    if (t && t.length > 0) tags = t;

    const { categoryPage, sortFilter } = store.getState();
    const { cateTags, productTags } = categoryPage;
    const [topicTagEntryIds, prodTagEntryIds] = getTopicAndProductTags(tags, cateTags, productTags);

    const response = await getSearch({
      ...sortFilter,
      keyword,
      topicTagEntryIds,
      prodTagEntryIds,
      language: lang,
    });

    if (response?.data) {
      store.dispatch(changeSearch(response.data));
    } else {
      console.warn('No search data returned.');
    }
  }

  const { searchPage } = store.getState();

  return {
    props: {
      page: searchPage.search,
      query: ctx.query,
      // promotion banner and popup banner for new layout
      promotionBannerData: promotionBannerData || null,
      popUpBannerData: popUpBannerData || null,
    },
  };
});
